// src/data/deskClocks.js
//
// MASTER LIST ITEM 6 (Bob 2026-10-14: "address these immediately, properly, accurately, and securely"): withdrawn
// claims, the carrier / appeal / Board clocks, and the Board stage, on BOTH officers' desks (LCDesk, GCDesk). Pure - no
// React, no Firestore - and proved by scripts\test-desk-clocks.mjs. Nothing here is computed afresh: every clock is the
// one the union calendar already reads (unionCalendar.deadlinesFromClaims, which carries claimClocks.appealDeadlines),
// and the Board stage is boardStage.lifecycleStanding. The desks show what the platform already knows, in one place.
//
// PD-4 holds: a claim with no computable clock is not shown as safe - it simply has no row here, and the desk's own
// "No filing window on record" block already lists it.
import { deadlinesFromClaims } from './unionCalendar';
import { lifecycleStanding } from './boardStage';
import { claimActs } from './offProperty';

const s = (v) => (v === undefined || v === null ? '' : String(v).trim());
const DAY = 86400000;
// Stages a claim is finished at on the property. A clock on such a claim is not a desk item - EXCEPT the Board, which
// is exactly the case boardStage exists for (a claim closed on the property while it is before a Board).
const FINISHED = ['closure', 'archived'];

function daysLeft(isoDate, nowMs) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s(isoDate))) return null;
  const due = Date.parse(isoDate + 'T23:59:59Z');
  return Math.floor((due - nowMs) / DAY);
}

// WITHDRAWN (ruling 307): the member withdrew her submission to the union. The claim's status does not change - the
// projection carriageLastAct (carriage.js claimProjection, written by the carriageAct function) says so. A claim
// whose acts predate the projection carries no carriageLastAct and is not shown here; the claim page still reads its acts.
export function isWithdrawn(c) {
  return s(c && c.carriageLastAct) === 'withdrawn';
}

// The withdrawn claims, newest withdrawal first, each with its filing-window days left - ruling 307 item 3: the union
// may still file the same claim itself while the window is open, so the window is the fact the officer needs.
export function withdrawnRows(claims, nowMs) {
  const now = typeof nowMs === 'number' ? nowMs : Date.now();
  return (Array.isArray(claims) ? claims : [])
    .filter(isWithdrawn)
    .map((c) => {
      const exp = s(c.expiresAt).slice(0, 10);
      return { claim: c, since: s(c.carriageSince), windowLeft: daysLeft(exp, now), windowCloses: exp || null };
    })
    .sort((a, b) => b.since.localeCompare(a.since));
}

// The clocks running on the desk's claims - the carrier's answer, the appeal ladder's deadlines and the Board's - one
// row each, OVERDUE FIRST, then soonest. Each row keeps the title the calendar gives it, which already names the clock,
// its day-count and its cite, and whose time it is (party).
export function deskClockRows(claims, nowMs) {
  const now = typeof nowMs === 'number' ? nowMs : Date.now();
  const live = (Array.isArray(claims) ? claims : []).filter((c) => c && s(c.status) && s(c.status) !== 'draft' && FINISHED.indexOf(s(c.status)) === -1);
  return deadlinesFromClaims(live)
    .map((d) => ({ ...d, left: daysLeft(d.date, now), party: d.party || (/^Carrier answer due/.test(s(d.title)) ? 'carrier' : '') }))
    .filter((d) => d.left !== null)
    .sort((a, b) => (a.left < 0) !== (b.left < 0) ? (a.left < 0 ? -1 : 1) : a.left - b.left);
}

// THE BOARD STAGE (ruling 235(b), boardStage.js - built, and until today wired to no screen). Claims before a Board by
// their own appeal acts, and - the honesty flag - any of them the on-property status shows as finished.
export function boardRows(claims) {
  const out = { before: [], misreported: [] };
  for (const c of Array.isArray(claims) ? claims : []) {
    if (!c || s(c.status) === 'draft') continue;
    const st = lifecycleStanding(s(c.status), 'claim', { appealActs: claimActs(c) });
    if (!st.beforeBoard) continue;
    const row = { claim: c, label: st.label, standing: st.appealStanding && st.appealStanding.label };
    out.before.push(row);
    if (st.misreportedAsFinished) out.misreported.push(row);
  }
  return out;
}
